<template>
    <div class="mt-15">
      <v-container>
        <!-- Logistics Summary -->
        <v-card class="mb-4">
          <v-toolbar
            dark
            color="primary darken-1"
            class="mt-2 mb-3"
            :extended="$vuetify.breakpoint.smAndDown"
          >
            <h2 class="headline font-weight-bold">Logistics Summary</h2>
            <v-spacer></v-spacer>
            <v-divider
              class="mx-4"
              inset
              vertical
              v-if="$vuetify.breakpoint.mdAndUp"
            ></v-divider>
            <v-spacer></v-spacer>
            <v-spacer v-if="$vuetify.breakpoint.mdAndUp"></v-spacer>
            
            <template v-slot:extension v-if="$vuetify.breakpoint.smAndDown">
              <v-select
                v-model="selectedWarehouse"
                :items="warehouseOptions"
                item-value="name"
                item-text="desc"
                label="Select Warehouse"
                return-object
                @change="fetchData"
                class="mt-7"
              ></v-select>
            </template>
            <v-select
              v-if="$vuetify.breakpoint.mdAndUp"
              v-model="selectedWarehouse"
              :items="warehouseOptions"
              item-value="name"
              item-text="desc"
              label="Select Warehouse"
              return-object
              @change="fetchData"
              class="mt-7"
            >
            </v-select>
           
            <v-spacer></v-spacer>
            <v-btn icon fab @click="openFilterDialog">
              <v-icon>mdi-filter-menu-outline</v-icon>
            </v-btn>
            <v-btn right icon>
              <v-icon>mdi-dots-vertical</v-icon>
            </v-btn>
          </v-toolbar>
  
          <!-- Display selected filters -->
          <v-expand-transition>
            <v-sheet
              v-if="hasSelectedFilters"
              class="pa-3 mb-3"
              color="grey lighten-4"
              rounded
            >
              <v-row align="center" no-gutters>
                <v-col cols="auto" class="mr-3">
                  <v-icon color="primary">mdi-filter</v-icon>
                  <span class="font-weight-medium ml-2">Active Filters:</span>
                </v-col>
                <v-col>
                  <v-chip-group>
                    <v-chip
                      v-for="filter in formattedFilters"
                      :key="filter.label"
                      class="ma-1"
                      small
                      outlined
                      color="primary"
                      close
                      @click:close="removeFilter(filter)"
                      close-icon="mdi-close-circle-outline" 
                      close-color="red" 
                    >
                      <v-icon left small color="primary">{{ getFilterIcon(filter.label) }}</v-icon>
                      <strong>{{ filter.label }}:</strong> {{ filter.value }}
                    </v-chip>
                  </v-chip-group>
                </v-col>
                <v-col cols="auto">
                  <v-btn
                    small
                    text
                    color="primary"
                    @click="clearAllFilters"
                  >
                    Clear All
                  </v-btn>
                </v-col>
              </v-row>
            </v-sheet>
          </v-expand-transition>
  
          <v-card-text>
            <v-row>
              <v-col cols="6" sm="3">
                <v-stat-card
                  title="Total Shipments"
                  :value="shipments.length"
                  color="primary"
                  icon="mdi-truck"
                />
              </v-col>
              <v-col cols="6" sm="3">
                <v-stat-card
                  title="In Transit"
                  :value="inTransitCount"
                  color="info"
                  icon="mdi-road-variant"
                />
              </v-col>
              <v-col cols="6" sm="3">
                <v-stat-card
                  title="Delayed"
                  :value="delayedCount"
                  color="warning"
                  icon="mdi-clock-alert"
                />
              </v-col>
              <v-col cols="6" sm="3">
                <v-stat-card
                  title="On-Time Delivery Rate"
                  :value="onTimeDeliveryRate + '%'"
                  color="success"
                  icon="mdi-clock-check"
                />
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
  
        <!-- Filters Dialog -->
        <v-dialog v-model="filterDialog" max-width="750px" persistent fullscreen-breakpoint="sm">
          <v-card class="mb-4">
            <v-toolbar color="primary" dark>
              <v-toolbar-title>Filters</v-toolbar-title>
              <v-spacer></v-spacer>
              <v-btn icon @click="closeFilterDialog">
                <v-icon>mdi-close</v-icon>
              </v-btn>
            </v-toolbar>
            
            <v-card-text>
              <v-form @submit.prevent="applyFilters">
                <v-row>
                  <v-col cols="12">
                    <v-autocomplete
                      v-model="selectedCarriers"
                      :items="carriers"
                      label="Carriers"
                      multiple
                      chips
                      small-chips
                      deletable-chips
                      clearable
                    ></v-autocomplete>
                  </v-col>
                  <v-col cols="12">
                    <v-autocomplete
                      v-model="selectedDestinations"
                      :items="destinations"
                      label="Destinations"
                      multiple
                      chips
                      small-chips
                      deletable-chips
                      clearable
                    ></v-autocomplete>
                  </v-col>
                  <v-col cols="12">                 
                    <v-autocomplete
                      v-model="selectedStatuses"
                      :items="statusOptions"
                      label="Statuses"
                      multiple
                      chips
                      small-chips
                      deletable-chips
                      clearable
                      class="mt-2"
                      dense
                    >
                      <template v-slot:selection="{ attrs, item, select, selected }">
                        <v-chip
                          v-bind="attrs"
                          :input-value="selected"
                          close
                          @click="select"
                          @click:close="removeStatus(item)"
                          outlined
                          small
                          class="ma-1"
                        >
                          {{ item }}
                        </v-chip>
                      </template>
                    </v-autocomplete>
                  </v-col>
                  <v-col cols="12" sm="6">
                    <v-menu
                      v-model="fromDateMenu"
                      :close-on-content-click="false"
                      transition="scale-transition"
                      offset-y
                      min-width="auto"
                    >
                      <template v-slot:activator="{ on, attrs }">
                        <v-text-field
                          v-model="fromDate"
                          label="From Date"
                          prepend-icon="mdi-calendar"
                          readonly
                          v-bind="attrs"
                          v-on="on"
                          clearable
                          @click:clear="fromDate = null"
                        ></v-text-field>
                      </template>
                      <v-date-picker
                        v-model="fromDate"
                        @input="fromDateMenu = false"
                        no-title
                        scrollable
                      ></v-date-picker>
                    </v-menu>
                  </v-col>
                  <v-col cols="12" sm="6">
                    <v-menu
                      v-model="toDateMenu"
                      :close-on-content-click="false"
                      transition="scale-transition"
                      offset-y
                      min-width="auto"
                    >
                      <template v-slot:activator="{ on, attrs }">
                        <v-text-field
                          v-model="toDate"
                          label="To Date"
                          prepend-icon="mdi-calendar"
                          readonly
                          v-bind="attrs"
                          v-on="on"
                          clearable
                          @click:clear="toDate = null"
                        ></v-text-field>
                      </template>
                      <v-date-picker
                        v-model="toDate"
                        @input="toDateMenu = false"
                        no-title
                        scrollable
                      ></v-date-picker>
                    </v-menu>
                  </v-col>
                </v-row>
              </v-form>
            </v-card-text>
            
            <v-card-actions>
              <v-row justify="space-between">
                <v-col cols="6" sm="3">
                  <v-btn block color="primary" @click="applyFilters" :loading="isLoading">
                    Apply 
                  </v-btn>
                </v-col>
                <v-col cols="6" sm="3">
                  <v-btn block color="primary" @click="fetchData" :loading="isLoading">
                    Fetch 
                  </v-btn>
                </v-col>
                <v-col cols="6" sm="3">
                  <v-btn block outlined color="error" @click="clearFilters" :disabled="isLoading">
                    Clear 
                  </v-btn>
                </v-col>
                <v-col cols="6" sm="3">
                  <v-btn block outlined color="warning" @click="closeFilterDialog" :disabled="isLoading">
                    Cancel
                  </v-btn>
                </v-col>
              </v-row>
            </v-card-actions>
          </v-card>
        </v-dialog>
  
        <!-- Shipments List -->
        <v-toolbar
          dark
          color="primary darken-1"
          class="mt-2 mb-3"
          :extended="$vuetify.breakpoint.smAndDown"
        >  
          <h2 class="headline font-weight-bold">
            Shipments
          </h2>
          <v-spacer></v-spacer>
          <v-divider
            class="mx-4"
            inset
            vertical
            v-if="$vuetify.breakpoint.mdAndUp"
          ></v-divider>
          
          <v-spacer></v-spacer>
  
          <template v-slot:extension v-if="$vuetify.breakpoint.smAndDown">
            <v-text-field
              v-model="search"
              hide-details
              append-icon="mdi-magnify"
              single-line
              label="Search"
              :loading="isSearching"       
            ></v-text-field> 
          </template>
          <v-text-field
            v-if="$vuetify.breakpoint.mdAndUp"
            v-model="search"
            hide-details
            append-icon="mdi-magnify"
            single-line
            label="Search"
            :loading="isSearching"       
          ></v-text-field> 
  
          <v-spacer></v-spacer>
          <v-btn icon fab large @click="addNewShipment">
            <v-icon>mdi-plus</v-icon>
          </v-btn> 
          <v-btn icon>
            <v-icon>mdi-dots-vertical</v-icon>
          </v-btn>
        </v-toolbar>       
        <v-row>
          <v-col cols="12" sm="6" md="4" v-for="shipment in filteredShipments" :key="shipment.id">
            <v-card class="mb-4 d-flex flex-column shipment-card">
              <v-card-title class="subtitle-1 font-weight-bold">
                {{ shipment.tracking_number }}
                <v-spacer></v-spacer>
                <v-chip :color="getStatusColor(shipment.status)" small>
                  {{ shipment.status }}
                </v-chip>
              </v-card-title>
              <v-card-subtitle>{{ shipment.customer_name }}</v-card-subtitle>
              <v-card-text class="flex-grow-1">
                <v-row dense>
                  <v-col cols="12" sm="6">
                    <v-icon small class="mr-1">mdi-map-marker</v-icon>{{ shipment.destination }}<br>
                    <v-icon small class="mr-1">mdi-truck</v-icon>{{ shipment.carrier }}<br>
                    <v-icon small class="mr-1">mdi-package-variant</v-icon>{{ shipment.package_type }}
                  </v-col>
                  <v-col cols="12" sm="6">
                    <strong>Est. Delivery:</strong> {{ formatDate(shipment.estimated_delivery) }}<br>
                    <strong>Shipped:</strong> {{ formatDate(shipment.ship_date) }}
                  </v-col>
                </v-row>
              </v-card-text>
              <v-card-actions>
                <v-btn icon fab color="primary" @click="updateShipmentStatus(shipment)">
                  <v-icon left>mdi-update</v-icon>
                </v-btn>
                <v-btn icon fab color="secondary" @click="viewShipmentHistory(shipment)">
                  <v-icon left>mdi-history</v-icon>
                </v-btn>
                <v-btn icon fab color="info" @click="editShipment(shipment)">
                  <v-icon left>mdi-pencil</v-icon>
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-col>
        </v-row>
      </v-container>
  
      <!-- Update Shipment Status Dialog -->
      <v-dialog v-model="updateStatusDialog" max-width="500px" fullscreen-breakpoint="xs">
        <v-card>
          <v-card-title>Update Shipment Status</v-card-title>
          <v-card-text>
            <v-form ref="updateStatusForm" v-model="updateStatusFormValid">
              <v-select
                v-model="updatedStatus.status"
                :items="statusOptions"
                label="Status"
                required
              ></v-select>
              <v-textarea
                v-model="updatedStatus.notes"
                label="Notes"
                required
              ></v-textarea>
              <v-text-field
                v-model="updatedStatus.location"
                label="Current Location"
                required
              ></v-text-field>
            </v-form>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="blue darken-1" text @click="closeUpdateStatusDialog">Cancel</v-btn>
            <v-btn color="blue darken-1" text @click="saveUpdatedStatus">Save</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
      <!-- View Shipment History Dialog -->
    <v-dialog v-model="viewHistoryDialog" max-width="700px" fullscreen-breakpoint="xs">
      <v-card>
        <v-card-title>Shipment History</v-card-title>
        <v-card-text>
          <v-timeline dense>
            <v-timeline-item
              v-for="event in selectedShipment.history"
              :key="event.id"
              :color="getStatusColor(event.status)"
              small
            >
              <template v-slot:opposite>
                {{ formatDate(event.timestamp) }}
              </template>
              <v-card class="elevation-2">
                <v-card-title class="text-h6">{{ event.status }}</v-card-title>
                <v-card-text>
                  {{ event.notes }}
                  <div class="mt-2">
                    <strong>Location:</strong> {{ event.location }}
                  </div>
                </v-card-text>
              </v-card>
            </v-timeline-item>
          </v-timeline>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="blue darken-1" text @click="viewHistoryDialog = false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Edit Shipment Dialog -->
    <v-dialog v-model="editShipmentDialog" max-width="500px" fullscreen-breakpoint="xs">
      <v-card>
        <v-card-title>Edit Shipment</v-card-title>
        <v-card-text>
          <v-form ref="editShipmentForm" v-model="editShipmentFormValid">
            <v-text-field v-model="editedShipment.tracking_number" label="Tracking Number" required></v-text-field>
            <v-text-field v-model="editedShipment.customer_name" label="Customer Name" required></v-text-field>
            <v-text-field v-model="editedShipment.destination" label="Destination" required></v-text-field>
            <v-select v-model="editedShipment.carrier" :items="carriers" label="Carrier" required></v-select>
            <v-select v-model="editedShipment.package_type" :items="packageTypes" label="Package Type" required></v-select>
            <v-select v-model="editedShipment.status" :items="statusOptions" label="Status" required></v-select>
            <v-date-picker v-model="editedShipment.estimated_delivery" label="Estimated Delivery Date" required></v-date-picker>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="blue darken-1" text @click="closeEditDialog">Cancel</v-btn>
          <v-btn color="blue darken-1" text @click="saveEditedShipment">Save</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Add New Shipment Dialog -->
    <v-dialog v-model="addShipmentDialog" max-width="500px" fullscreen-breakpoint="xs">
      <v-card>
        <v-card-title>Add New Shipment</v-card-title>
        <v-card-text>
          <v-form ref="addShipmentForm" v-model="addShipmentFormValid">
            <v-text-field v-model="newShipment.tracking_number" label="Tracking Number" required></v-text-field>
            <v-text-field v-model="newShipment.customer_name" label="Customer Name" required></v-text-field>
            <v-text-field v-model="newShipment.destination" label="Destination" required></v-text-field>
            <v-select v-model="newShipment.carrier" :items="carriers" label="Carrier" required></v-select>
            <v-select v-model="newShipment.package_type" :items="packageTypes" label="Package Type" required></v-select>
            <v-select v-model="newShipment.status" :items="statusOptions" label="Status" required></v-select>
            <v-date-picker v-model="newShipment.estimated_delivery" label="Estimated Delivery Date" required></v-date-picker>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="blue darken-1" text @click="closeAddDialog">Cancel</v-btn>
          <v-btn color="blue darken-1" text @click="saveNewShipment">Save</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import moment from 'moment';
import VStatCard from '@/components/VStatCard.vue';
import dynamic_axios from "../services/dynamic_axios";

export default {
  components: {
    VStatCard
  },
  data() {
    return {
      shipments: [
        {
          id: 1,
          tracking_number: "TRK123456",
          customer_name: "John Doe",
          destination: "New York, NY",
          carrier: "FedEx",
          package_type: "Box",
          status: "In Transit",
          estimated_delivery: "2024-07-05",
          ship_date: "2024-06-29",
          history: [
            {
              id: 1,
              status: "Shipped",
              notes: "Package picked up",
              location: "Los Angeles, CA",
              timestamp: "2024-06-29T10:00:00"
            },
            {
              id: 2,
              status: "In Transit",
              notes: "Package in transit",
              location: "Chicago, IL",
              timestamp: "2024-07-01T14:30:00"
            }
          ]
        },
        // Add more dummy shipments here
      ],
      selectedWarehouse: null,
      warehouseOptions: [
        { name: 'la_warehouse', desc: 'Los Angeles Warehouse' },
        { name: 'ny_warehouse', desc: 'New York Warehouse' }
      ],
      isLoading: false,
      filterDialog: false,
      updateStatusDialog: false,
      viewHistoryDialog: false,
      selectedShipment: '',
      updatedStatus: {
        status: "",
        notes: "",
        location: ""
      },
      editShipmentFormValid: false,
      addShipmentFormValid: false,
      updateStatusFormValid: false,
      statusOptions: ["Pending", "Shipped", "In Transit", "Out for Delivery", "Delivered", "Delayed", "Returned"],
      carriers: ["FedEx", "UPS", "USPS", "DHL"],
      packageTypes: ["Box", "Envelope", "Pallet", "Tube"],
      editShipmentDialog: false,
      addShipmentDialog: false,
      editedShipment: {},
      newShipment: {
        tracking_number: "",
        customer_name: "",
        destination: "",
        carrier: "",
        package_type: "",
        status: "Pending",
        estimated_delivery: moment().add(7, 'days').format('YYYY-MM-DD'),
      },
      selectedCarriers: [],
      selectedDestinations: [],
      selectedStatuses: [],
      isSearching: false,
      search: null,
      fromDateMenu: false,
      toDateMenu: false,
      fromDate: new Date(new Date().getFullYear(), new Date().getMonth(), 2).toISOString().substr(0, 10),
      toDate: new Date(Date.now() - (new Date()).getTimezoneOffset() * 60000).toISOString().substr(0, 10),
      destinations: ["New York, NY", "Los Angeles, CA", "Chicago, IL", "Houston, TX", "Miami, FL"],
    };
  },
  computed: {
    inTransitCount() {
      return this.shipments.filter(shipment => shipment.status === "In Transit").length;
    },
    delayedCount() {
      return this.shipments.filter(shipment => shipment.status === "Delayed").length;
    },
    onTimeDeliveryRate() {
      const deliveredOnTime = this.shipments.filter(shipment => 
        shipment.status === "Delivered" && moment(shipment.estimated_delivery).isSameOrAfter(moment(shipment.ship_date), 'day')
      ).length;
      const totalDelivered = this.shipments.filter(shipment => shipment.status === "Delivered").length;
      return totalDelivered > 0 ? ((deliveredOnTime / totalDelivered) * 100).toFixed(2) : 0;
    },
    filteredShipments() {
      return this.shipments.filter(shipment => 
        (this.selectedCarriers.length === 0 || this.selectedCarriers.includes(shipment.carrier)) &&
        (this.selectedDestinations.length === 0 || this.selectedDestinations.includes(shipment.destination)) &&
        (this.selectedStatuses.length === 0 || this.selectedStatuses.includes(shipment.status))
      );
    },
    hasSelectedFilters() {
      return this.selectedStatuses.length > 0 || this.selectedCarriers.length > 0 || 
             this.selectedDestinations.length > 0 || this.fromDate || this.toDate;
    },
    formattedFilters() {
      const filters = [];
      if (this.selectedStatuses.length > 0) {
        filters.push({ label: 'Statuses', value: this.selectedStatuses.join(', ') });
      }
      if (this.selectedCarriers.length > 0) {
        filters.push({ label: 'Carriers', value: this.selectedCarriers.join(', ') });
      }
      if (this.selectedDestinations.length > 0) {
        filters.push({ label: 'Destinations', value: this.selectedDestinations.join(', ') });
      }
      if (this.fromDate) {
        filters.push({ label: 'From Date', value: this.fromDate });
      }
      if (this.toDate) {
        filters.push({ label: 'To Date', value: this.toDate });
      }
      return filters;
    }
  },
  methods: {
    async fetchData() {
      this.loading = true;
      try {
        const warehouse = this.selectedWarehouse?.name;
        const params = new URLSearchParams({
          warehouse
        }).toString();
        const response = await dynamic_axios.get(`/logistics/shipments?${params}`, {
          apiName: warehouse  // Specify which API configuration to use
        });
        this.shipments = response.data;
      } catch (error) {
        console.error('Error fetching data:', error);
        // You might want to add error handling here, such as displaying a snackbar
      } finally {
        this.loading = false;
      }
    },
    formatDate(date) {
      return moment(date).format('MMM DD, YYYY');
    },
    getStatusColor(status) {
      const colors = {
        "Pending": "grey",
        "Shipped": "primary",
        "In Transit": "info",
        "Out for Delivery": "warning",
        "Delivered": "success",
        "Delayed": "error",
        "Returned": "orange"
      };
      return colors[status] || "grey";
    },
    openFilterDialog() {
      this.filterDialog = true;
    },
    closeFilterDialog() {
      this.filterDialog = false;
    },
    updateShipmentStatus(shipment) {
      this.selectedShipment = shipment;
      this.updateStatusDialog = true;
    },
    closeUpdateStatusDialog() {
      this.updateStatusDialog = false;
      this.updatedStatus = {
        status: "",
        notes: "",
        location: ""
      };
    },
    saveUpdatedStatus() {
      if (this.$refs.updateStatusForm.validate()) {
        const statusUpdate = {
          id: Date.now(),
          ...this.updatedStatus,
          timestamp: moment().format()
        };
        this.selectedShipment.history.unshift(statusUpdate);
        this.selectedShipment.status = statusUpdate.status;
        this.closeUpdateStatusDialog();
        this.$toast.success("Shipment status updated successfully.");
      }
    },
    viewShipmentHistory(shipment) {
      this.selectedShipment = shipment;
      this.viewHistoryDialog = true;
    },
    editShipment(shipment) {
      this.editedShipment = { ...shipment };
      this.editShipmentDialog = true;
    },
    applyFilters() {
      // Reset to the first page of results
      this.currentPage = 1;

      console.log('Filters applied:', {
        carriers: this.selectedCarriers,
        destinations: this.selectedDestinations,
        statuses: this.selectedStatuses
      });

      // Update URL to reflect current filters
      this.$router.push({ 
        query: { 
          carriers: this.selectedCarriers.join(','),
          destinations: this.selectedDestinations.join(','),
          statuses: this.selectedStatuses.join(',')
        }
      });

      // Emit event for parent components
      this.$emit('filters-changed', {
        carriers: this.selectedCarriers,
        destinations: this.selectedDestinations,
        statuses: this.selectedStatuses
      });
    },
    clearFilters() {
      this.selectedCarriers = [];
      this.selectedDestinations = [];
      this.selectedStatuses = [];
      this.fromDate = null;
      this.toDate = null;
    },
    saveEditedShipment() {
      if (this.$refs.editShipmentForm.validate()) {
        const index = this.shipments.findIndex(s => s.id === this.editedShipment.id);
        if (index !== -1) {
          this.shipments.splice(index, 1, this.editedShipment);
          this.$toast.success("Shipment updated successfully.");
          this.editShipmentDialog = false;
        }
      }
    },
    addNewShipment() {
      this.addShipmentDialog = true;
    },
    saveNewShipment() {
      if (this.$refs.addShipmentForm.validate()) {
        const newShipment = {
          id: Date.now(),
          ...this.newShipment,
          ship_date: moment().format('YYYY-MM-DD'),
          history: []
        };
        this.shipments.unshift(newShipment);
        this.$toast.success("New shipment added successfully.");
        this.addShipmentDialog = false;
        this.resetNewShipmentForm();
      }
    },
    resetNewShipmentForm() {
      this.newShipment = {
        tracking_number: "",
        customer_name: "",
        destination: "",
        carrier: "",
        package_type: "",
        status: "Pending",
        estimated_delivery: moment().add(7, 'days').format('YYYY-MM-DD'),
      };
    },
    closeEditDialog() {
      this.editShipmentDialog = false;
    },
    closeAddDialog() {
      this.addShipmentDialog = false;
      this.resetNewShipmentForm();
    },
    removeStatus(item) {
      this.selectedStatuses.splice(this.selectedStatuses.indexOf(item), 1)
    },
    getFilterIcon(label) {
      const icons = {
        'Statuses': 'mdi-flag',
        'Carriers': 'mdi-truck',
        'Destinations': 'mdi-map-marker',
        'From Date': 'mdi-calendar-start',
        'To Date': 'mdi-calendar-end'
      };
      return icons[label] || 'mdi-filter-variant';
    },
    removeFilter(filter) {
      switch(filter.label) {
        case 'Statuses':
          this.selectedStatuses = [];
          break;
        case 'Carriers':
          this.selectedCarriers = [];
          break;
        case 'Destinations':
          this.selectedDestinations = [];
          break;
        case 'From Date':
          this.fromDate = null;
          break;
        case 'To Date':
          this.toDate = null;
          break;
      }
    },
    clearAllFilters() {
      this.selectedStatuses = [];
      this.selectedCarriers = [];
      this.selectedDestinations = [];
      this.fromDate = null;
      this.toDate = null;
    },
  }
};
</script>

<style scoped>
.shipment-card {
  height: 100%; /* Make all cards take full height of their container */
}
.max-width-300 {
  max-width: 300px;
}

.v-card__text {
  overflow: auto; /* Add scrollbar if content overflows */
}
.v-card {
  transition: all 0.3s;
}
.v-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 4px 8px rgba(0,0,0,0.1);
}
/* Responsive styles */
@media (max-width: 600px) {
  .v-card-title {
    font-size: 1.1rem !important;
  }
  .v-card-subtitle {
    font-size: 0.9rem !important;
  }
  .v-card-text {
    font-size: 0.85rem !important;
  }
  .v-btn {
    font-size: 0.8rem !important;
  }
}
</style>